import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Facebook, Instagram, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";

type Scan = {
  id: string;
  platform: "instagram" | "facebook" | string;
  user_agent: string | null;
  referrer: string | null;
  scanned_at: string;
};

const AdminQrScans = () => {
  const navigate = useNavigate();
  const [scans, setScans] = useState<Scan[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("qr_scans")
      .select("id, platform, user_agent, referrer, scanned_at")
      .order("scanned_at", { ascending: false })
      .limit(500);
    if (error) {
      toast.error("Could not load scans. You may not have admin access.");
      setScans([]);
    } else {
      setScans((data ?? []) as Scan[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate("/admin", { replace: true });
        return;
      }
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", sessionData.session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!mounted) return;
      if (!roles) {
        toast.error("Your account is not an admin. Ask the owner to grant access.");
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      load();
    })();
    return () => { mounted = false; };
  }, [navigate]);

  const stats = useMemo(() => {
    if (!scans) return { total: 0, instagram: 0, facebook: 0 };
    return {
      total: scans.length,
      instagram: scans.filter((s) => s.platform === "instagram").length,
      facebook: scans.filter((s) => s.platform === "facebook").length,
    };
  }, [scans]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/admin", { replace: true });
  };

  return (
    <main className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-10 max-w-6xl">
        <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-primary">QR Scan Analytics</h1>
            <p className="text-muted-foreground">Tracks visits to /instagram and /facebook</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading || !authorized}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="ghost" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </header>

        {!authorized && !loading && (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <p className="text-muted-foreground">
              You're signed in but not an admin. Ask the site owner to add your user ID to the admin role.
            </p>
          </div>
        )}

        {authorized && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-card border border-border rounded-xl p-6">
                <p className="text-sm text-muted-foreground">Total scans</p>
                <p className="text-3xl font-bold text-primary mt-1">{stats.total}</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Instagram className="w-4 h-4" /> Instagram
                </div>
                <p className="text-3xl font-bold text-primary mt-1">{stats.instagram}</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Facebook className="w-4 h-4" /> Facebook
                </div>
                <p className="text-3xl font-bold text-primary mt-1">{stats.facebook}</p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Referrer</TableHead>
                      <TableHead>User Agent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
                    )}
                    {!loading && scans && scans.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No scans yet.</TableCell></TableRow>
                    )}
                    {!loading && scans?.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="whitespace-nowrap text-sm">{new Date(s.scanned_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={s.platform === "instagram" ? "default" : "secondary"}>{s.platform}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs" title={s.referrer ?? ""}>{s.referrer || "—"}</TableCell>
                        <TableCell className="max-w-[300px] truncate text-xs" title={s.user_agent ?? ""}>{s.user_agent || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default AdminQrScans;